import { ApiError, ERROR_CODES, isApiError } from '@luacentrix/shared';
import type {
  BuildLoaderInput,
  BuildLoaderResponse,
  CreateLicenseData,
  CreateProjectData,
  CreateScriptInput,
  DeleteScriptResponse,
  DiscardScriptUploadResponse,
  HealthResponse,
  KeysystemConfigResponse,
  LicenseIssueResponse,
  ListLicensesQuery,
  ListProjectsQuery,
  Paginated,
  PaginationQuery,
  Project,
  License,
  ObfuscateScriptInput,
  ObfuscateScriptResponse,
  RevokeLicenseData,
  SaveKeysystemInput,
  ScriptListResponse,
  ScriptResponse,
  ScriptUploadResponse,
  StatusResponse,
  UpdateScriptPlacesInput,
} from '@luacentrix/contracts';
import {
  DEFAULT_PAGE_SIZE,
  type PageFetcher,
  collectPages,
  firstPage,
  iteratePages,
  mergeQuery,
} from './pagination';
import {
  apiErrorFromResponse,
  apiErrorFromTransport,
  echoRequestId,
  parseErrorEnvelope,
} from './envelope';
import {
  assertAccountSession,
  assertBuildLoaderResponse,
  assertKeysystemConfig,
  assertObfuscateScriptResponse,
  assertStatusResponse,
  assertDeleteScriptResponse,
  assertLicensePage,
  assertLicenseStatus,
  assertBlockPagePreview,
  assertDiscardScriptUploadResponse,
  assertOrderStatus,
  assertProjectPage,
  assertProjectStatus,
  assertScriptListResponse,
  assertScriptResponse,
  assertScriptUploadResponse,
  assertSecurityActionPage,
  assertSecurityBlock,
  assertSecurityBlockPage,
  assertSecurityEventPage,
  assertSecurityIpQuery,
  assertSecurityNotification,
  assertSecurityNotificationPage,
  assertSecurityPolicy,
  assertSecurityStats,
  assertSecurityStatus,
} from './guards';
import {
  buildHeaders,
  combineSignals,
  isSafeBaseUrl,
  joinUrl,
  newIdempotencyKey,
  newRequestId,
  type FetchImplementation,
} from './transport';
import type {
  AccountSessionResponse,
  AddSecurityAllowlistData,
  CreateSecurityBlockData,
  ExtendSecurityBlockData,
  ListSecurityActionsQuery,
  ListSecurityBlocksQuery,
  ListSecurityEventsQuery,
  ListSecurityNotificationsQuery,
  RemoveSecurityAllowlistData,
  RevokeSecurityBlockData,
  SecurityActionsPage,
  SecurityAllowlistResponse,
  SecurityBlockPagePreviewData,
  SecurityBlockPagePreviewResponse,
  SecurityBlocksPage,
  SecurityEventsPage,
  SecurityIpQueryQuery,
  SecurityIpQueryResponse,
  SecurityNotificationsResponse,
  SecurityPolicyResponse,
  SecurityStatsResponse,
  SecurityStatusQuery,
  SecurityStatusResponse,
  SignOutResponse,
  UpdateSecurityPolicyData,
} from './security';
import { AUTH_PATHS, SECURITY_PATHS } from './security';
import {
  SCRIPT_COLLECTION_PATH,
  SCRIPT_LOADER_PATH,
  SCRIPT_OBFUSCATE_PATH,
  scriptItemPath,
  scriptListQueryValues,
  scriptPlacesPath,
  type ScriptListQuery,
} from './scripts';
import {
  SCRIPT_UPLOAD_COLLECTION_PATH,
  UPLOAD_TIMEOUT_MS,
  scriptUploadItemPath,
  sendScriptUpload,
  type UploadProgress,
  type XhrFactory,
  type XhrLike,
} from './upload';
import type {
  AllowlistEntry,
  IpBlockRecord,
  SecurityNotificationRecord,
} from '@luacentrix/security';

export interface ApiClientConfig {
  baseUrl: string;
  fetch?: FetchImplementation | undefined;
  xhrFactory?: XhrFactory | undefined;
  accessToken?: (() => string | null | undefined | Promise<string | null | undefined>) | undefined;
  timeoutMs?: number | undefined;
  uploadTimeoutMs?: number | undefined;
  defaultHeaders?: Record<string, string> | undefined;
  credentials?: 'include' | 'same-origin' | 'omit' | undefined;
  requireHttps?: boolean | undefined;
}

export interface UploadOptions {
  signal?: AbortSignal | undefined;
  idempotencyKey?: string | undefined;
  timeoutMs?: number | undefined;
  onProgress?: ((progress: UploadProgress) => void) | undefined;
}

export interface RequestOptions {
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  query?: Record<string, string | undefined> | undefined;
  body?: unknown | undefined;
  signal?: AbortSignal | undefined;
  idempotencyKey?: string | undefined;
  timeoutMs?: number | undefined;
  guard?: ((payload: unknown, requestId: string) => void) | undefined;
}

export interface ApiClient {
  readonly config: ApiClientConfig;
  request<T>(options: RequestOptions): Promise<T>;
  health(signal?: AbortSignal): Promise<HealthResponse>;
  status(signal?: AbortSignal): Promise<StatusResponse>;
  projects: {
    listQuery: (query: ListProjectsQuery) => Record<string, string>;
    list(query?: ListProjectsQuery, options?: RequestOptionsSubset): Promise<Paginated<Project>>;
    page(query?: ListProjectsQuery): PageFetcher<Project>;
    iterate(query?: ListProjectsQuery): AsyncGenerator<Project, void, undefined>;
    create(data: CreateProjectData, options?: RequestOptionsSubset): Promise<Project>;
  };
  licenses: {
    listQuery: (query: ListLicensesQuery) => Record<string, string>;
    list(query?: ListLicensesQuery, options?: RequestOptionsSubset): Promise<Paginated<License>>;
    page(query?: ListLicensesQuery): PageFetcher<License>;
    iterate(query?: ListLicensesQuery): AsyncGenerator<License, void, undefined>;
    create(data: CreateLicenseData, options?: RequestOptionsSubset): Promise<LicenseIssueResponse>;
    revoke(
      licenseId: string,
      data: RevokeLicenseData,
      options?: RequestOptionsSubset,
    ): Promise<License>;
  };
  orders: {
    assertServerState(
      order: Paginated<import('@luacentrix/contracts').Order>['items'][number],
      requestId?: string,
    ): void;
  };
  account: {
    session(options?: RequestOptionsSubset): Promise<AccountSessionResponse>;
    signOut(options?: RequestOptionsSubset): Promise<SignOutResponse>;
  };
  keysystem: {
    get(options?: RequestOptionsSubset): Promise<KeysystemConfigResponse>;
    save(
      data: SaveKeysystemInput,
      options?: RequestOptionsSubset,
    ): Promise<KeysystemConfigResponse>;
  };
  uploads: {
    put(file: File, options?: UploadOptions): Promise<ScriptUploadResponse>;
    discard(uploadId: string, options?: RequestOptionsSubset): Promise<DiscardScriptUploadResponse>;
  };
  scripts: {
    listQuery: (query: ScriptListQuery) => Record<string, string>;
    list(
      query?: ScriptListQuery,
      options?: RequestOptionsSubset,
    ): Promise<ScriptListResponse>;
    create(
      data: CreateScriptInput,
      options?: RequestOptionsSubset,
    ): Promise<ScriptResponse>;
    updatePlaces(
      scriptId: string,
      data: UpdateScriptPlacesInput,
      options?: RequestOptionsSubset,
    ): Promise<ScriptResponse>;
    remove(scriptId: string, options?: RequestOptionsSubset): Promise<DeleteScriptResponse>;
    buildLoader(
      data: BuildLoaderInput,
      options?: RequestOptionsSubset,
    ): Promise<BuildLoaderResponse>;
    obfuscate(
      data: ObfuscateScriptInput,
      options?: RequestOptionsSubset,
    ): Promise<ObfuscateScriptResponse>;
  };
  security: {
    statusQuery: (query: SecurityStatusQuery) => Record<string, string>;
    status(
      query: SecurityStatusQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityStatusResponse>;
    events(
      query?: ListSecurityEventsQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityEventsPage>;
    blocks(
      query?: ListSecurityBlocksQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityBlocksPage>;
    block(data: CreateSecurityBlockData, options?: RequestOptionsSubset): Promise<IpBlockRecord>;
    unblock(
      blockId: string,
      data: RevokeSecurityBlockData,
      options?: RequestOptionsSubset,
    ): Promise<IpBlockRecord>;
    extend(
      blockId: string,
      data: ExtendSecurityBlockData,
      options?: RequestOptionsSubset,
    ): Promise<IpBlockRecord>;
    ip(
      query: SecurityIpQueryQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityIpQueryResponse>;
    stats(options?: RequestOptionsSubset): Promise<SecurityStatsResponse>;
    policy(options?: RequestOptionsSubset): Promise<SecurityPolicyResponse>;
    updatePolicy(
      data: UpdateSecurityPolicyData,
      options?: RequestOptionsSubset,
    ): Promise<SecurityPolicyResponse>;
    preview(
      data: SecurityBlockPagePreviewData,
      options?: RequestOptionsSubset,
    ): Promise<SecurityBlockPagePreviewResponse>;
    actions(
      query?: ListSecurityActionsQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityActionsPage>;
    notifications(
      query?: ListSecurityNotificationsQuery,
      options?: RequestOptionsSubset,
    ): Promise<SecurityNotificationsResponse>;
    acknowledgeNotification(
      notificationId: string,
      options?: RequestOptionsSubset,
    ): Promise<SecurityNotificationRecord>;
    dismissNotification(
      notificationId: string,
      options?: RequestOptionsSubset,
    ): Promise<SecurityNotificationRecord>;
    allowlist: {
      list(options?: RequestOptionsSubset): Promise<SecurityAllowlistResponse>;
      add(data: AddSecurityAllowlistData, options?: RequestOptionsSubset): Promise<AllowlistEntry>;
      remove(
        allowlistId: string,
        data: RemoveSecurityAllowlistData,
        options?: RequestOptionsSubset,
      ): Promise<void>;
    };
  };
}

export interface RequestOptionsSubset {
  signal?: AbortSignal | undefined;
  idempotencyKey?: string | undefined;
  timeoutMs?: number | undefined;
}

const KEYSYSTEM_PATH = '/api/v1/keysystem';

const MUTATION_METHODS: readonly RequestOptions['method'][] = ['POST', 'PUT', 'PATCH', 'DELETE'];

const ABSOLUTE_BASE_URL = /^https?:\/\//;

function queryValue(query: Record<string, string | undefined>): Record<string, string> {
  const output: Record<string, string> = {};
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') {
      output[key] = value;
    }
  }
  return output;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  if (config.baseUrl === '') {
    throw new ApiError(ERROR_CODES.CONFIGURATION_INVALID, { details: { field: 'baseUrl' } });
  }
  if (!ABSOLUTE_BASE_URL.test(config.baseUrl)) {
    throw new ApiError(ERROR_CODES.CONFIGURATION_INVALID, {
      details: { field: 'baseUrl', reason: 'absolute_origin' },
    });
  }
  if (config.requireHttps === true && !isSafeBaseUrl(config.baseUrl)) {
    throw new ApiError(ERROR_CODES.CONFIGURATION_INVALID, {
      details: { field: 'baseUrl', reason: 'insecure' },
    });
  }

  const doFetch =
    config.fetch ??
    ((input, init) => globalThis.fetch(input, init as Parameters<typeof globalThis.fetch>[1]));

  const request = async <T>(options: RequestOptions): Promise<T> => {
    const requestId = newRequestId();
    const idempotencyKey =
      options.idempotencyKey ??
      (MUTATION_METHODS.includes(options.method) ? newIdempotencyKey(options.path) : undefined);

    const accessToken = await resolveToken(config.accessToken);
    const { headers, requestId: outboundRequestId } = buildHeaders({
      requestId,
      ...(idempotencyKey === undefined ? {} : { idempotencyKey }),
      ...(accessToken === undefined ? {} : { accessToken }),
      extra: config.defaultHeaders,
      hasBody: options.body !== undefined,
    });

    const url = joinUrl(config.baseUrl, options.path, options.query);
    const { signal, stop, cancel, timedOut } = combineSignals(
      options.timeoutMs ?? config.timeoutMs ?? 15_000,
      options.signal,
    );

    let response: Response;
    let echoedId: string;
    let payload: unknown;
    try {
      response = (await doFetch(url, {
        method: options.method,
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        credentials: config.credentials ?? 'include',
        signal,
      })) as Response;
      echoedId = echoRequestId(response.headers, outboundRequestId);
      payload = await readBody(response);
    } catch (reason) {
      cancel();
      if (isApiError(reason)) {
        throw reason;
      }
      throw apiErrorFromTransport(reason, timedOut(), outboundRequestId);
    }
    stop();

    if (!response.ok) {
      throw apiErrorFromResponse(response, parseErrorEnvelope(payload), echoedId);
    }

    options.guard?.(payload, echoedId);
    return payload as T;
  };

  const listProjectsQuery = (query: ListProjectsQuery): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.search === undefined ? {} : { search: query.search }),
    });

  const listLicensesQuery = (query: ListLicensesQuery): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.project_id === undefined ? {} : { project_id: query.project_id }),
      ...(query.customer_id === undefined ? {} : { customer_id: query.customer_id }),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.include_expired === undefined
        ? {}
        : { include_expired: String(query.include_expired) }),
    });

  const projectsFetcher =
    (query: ListProjectsQuery): PageFetcher<Project> =>
    (pageQuery: PaginationQuery) =>
      request<Paginated<Project>>({
        method: 'GET',
        path: '/api/v1/projects',
        query: listProjectsQuery({ ...query, ...pageQuery }),
        guard: (payload, requestId) => {
          assertProjectPage(payload as Paginated<Project>, { field: 'project.page', requestId });
        },
      });

  const securityStatusQuery = (query: SecurityStatusQuery): Record<string, string> =>
    queryValue({ ref: query.ref });

  const listSecurityEventsQuery = (query: ListSecurityEventsQuery): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.severity === undefined ? {} : { severity: query.severity }),
      ...(query.type === undefined ? {} : { type: query.type }),
    });

  const listSecurityBlocksQuery = (query: ListSecurityBlocksQuery): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.kind === undefined ? {} : { kind: query.kind }),
      ...(query.search === undefined ? {} : { search: query.search }),
    });

  const listSecurityActionsQuery = (query: ListSecurityActionsQuery): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.target_id === undefined ? {} : { target_id: query.target_id }),
    });

  const listSecurityNotificationsQuery = (
    query: ListSecurityNotificationsQuery,
  ): Record<string, string> =>
    queryValue({
      limit: String(query.limit ?? DEFAULT_PAGE_SIZE),
      ...(query.cursor === undefined ? {} : { cursor: query.cursor }),
      ...(query.status === undefined ? {} : { status: query.status }),
      ...(query.kind === undefined ? {} : { kind: query.kind }),
      ...(query.severity === undefined ? {} : { severity: query.severity }),
    });

  const notificationAction = (
    notificationId: string,
    action: 'acknowledge' | 'dismiss',
    options: RequestOptionsSubset,
  ): Promise<SecurityNotificationRecord> =>
    request<SecurityNotificationRecord>({
      method: 'POST',
      path: `${SECURITY_PATHS.notifications}/${encodeURIComponent(notificationId)}/${action}`,
      body: {},
      ...(options.idempotencyKey === undefined ? {} : { idempotencyKey: options.idempotencyKey }),
      ...(options.signal === undefined ? {} : { signal: options.signal }),
      guard: (payload, requestId) => {
        assertSecurityNotification(payload as SecurityNotificationRecord, {
          field: `security.notification.${action}`,
          requestId,
        });
      },
    });

  const licensesFetcher =
    (query: ListLicensesQuery): PageFetcher<License> =>
    (pageQuery: PaginationQuery) =>
      request<Paginated<License>>({
        method: 'GET',
        path: '/api/v1/licenses',
        query: listLicensesQuery({ ...query, ...pageQuery }),
        guard: (payload, requestId) => {
          assertLicensePage(payload as Paginated<License>, { field: 'license.page', requestId });
        },
      });

  return {
    config,
    request,
    health: signal =>
      request<HealthResponse>({
        method: 'GET',
        path: '/healthz',
        ...(signal === undefined ? {} : { signal }),
      }),
    status: signal =>
      request<StatusResponse>({
        method: 'GET',
        path: '/api/v1/status',
        ...(signal === undefined ? {} : { signal }),
        guard: (payload, requestId) => {
          assertStatusResponse(payload, { field: 'status.response', requestId });
        },
      }),
    projects: {
      listQuery: listProjectsQuery,
      list: (query = {}, options = {}) =>
        request<Paginated<Project>>({
          method: 'GET',
          path: '/api/v1/projects',
          query: listProjectsQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertProjectPage(payload as Paginated<Project>, { field: 'project.page', requestId });
          },
        }),
      page: (query = {}) => projectsFetcher(query),
      iterate: (query = {}) => iteratePages<Project>(projectsFetcher(query)),
      create: (data, options = {}) =>
        request<Project>({
          method: 'POST',
          path: '/api/v1/projects',
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertProjectStatus(payload as Project, { field: 'project.create', requestId });
          },
        }),
    },
    licenses: {
      listQuery: listLicensesQuery,
      list: (query = {}, options = {}) =>
        request<Paginated<License>>({
          method: 'GET',
          path: '/api/v1/licenses',
          query: listLicensesQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertLicensePage(payload as Paginated<License>, { field: 'license.page', requestId });
          },
        }),
      page: (query = {}) => licensesFetcher(query),
      iterate: (query = {}) => iteratePages<License>(licensesFetcher(query)),
      create: (data, options = {}) =>
        request<LicenseIssueResponse>({
          method: 'POST',
          path: '/api/v1/licenses',
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            const receipt = payload as LicenseIssueResponse;
            if (typeof receipt.secret !== 'string' || receipt.secret.length < 12) {
              throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
                details: { field: 'license.secret', request_id: requestId },
              });
            }
            assertLicenseStatus(receipt.license, { field: 'license.create', requestId });
          },
        }),
      revoke: (licenseId, data, options = {}) =>
        request<License>({
          method: 'POST',
          path: `/api/v1/licenses/${encodeURIComponent(licenseId)}/revoke`,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertLicenseStatus(payload as License, { field: 'license.revoke', requestId });
          },
        }),
    },
    orders: {
      assertServerState: (order, requestId) => {
        assertOrderStatus(order, {
          field: 'order.status',
          ...(requestId === undefined ? {} : { requestId }),
        });
      },
    },
    account: {
      session: (options = {}) =>
        request<AccountSessionResponse>({
          method: 'GET',
          path: SECURITY_PATHS.session,
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertAccountSession(payload as AccountSessionResponse, {
              field: 'session.account',
              requestId,
            });
          },
        }),
      signOut: (options = {}) =>
        request<SignOutResponse>({
          method: 'POST',
          path: AUTH_PATHS.logout,
          body: {},
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            const receipt = payload as SignOutResponse;
            if (typeof receipt.revoked !== 'boolean') {
              throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
                details: { field: 'session.revoked', request_id: requestId },
              });
            }
          },
        }),
    },
    keysystem: {
      get: (options = {}) =>
        request<KeysystemConfigResponse>({
          method: 'GET',
          path: KEYSYSTEM_PATH,
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertKeysystemConfig(payload as KeysystemConfigResponse, {
              field: 'keysystem.get',
              requestId,
            });
          },
        }),
      save: (data, options = {}) =>
        request<KeysystemConfigResponse>({
          method: 'PUT',
          path: KEYSYSTEM_PATH,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertKeysystemConfig(payload as KeysystemConfigResponse, {
              field: 'keysystem.save',
              requestId,
            });
          },
        }),
    },
    uploads: {
      put: async (file, options = {}) => {
        const requestId = newRequestId();
        const accessToken = await resolveToken(config.accessToken);
        const payload = await sendScriptUpload<ScriptUploadResponse>({
          baseUrl: config.baseUrl,
          path: SCRIPT_UPLOAD_COLLECTION_PATH,
          file,
          ...(accessToken === undefined ? {} : { accessToken }),
          credentials: config.credentials ?? 'include',
          timeoutMs: options.timeoutMs ?? config.uploadTimeoutMs ?? UPLOAD_TIMEOUT_MS,
          requestId,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.onProgress === undefined ? {} : { onProgress: options.onProgress }),
          createRequest: config.xhrFactory ?? defaultXhrFactory,
        });
        assertScriptUploadResponse(payload, { field: 'script_upload.create', requestId });
        return payload;
      },
      discard: (uploadId, options = {}) =>
        request<DiscardScriptUploadResponse>({
          method: 'DELETE',
          path: scriptUploadItemPath(uploadId),
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertDiscardScriptUploadResponse(payload as DiscardScriptUploadResponse, {
              field: 'script_upload.discard',
              requestId,
            });
          },
        }),
    },
    scripts: {
      listQuery: scriptListQueryValues,
      list: (query = {}, options = {}) =>
        request<ScriptListResponse>({
          method: 'GET',
          path: SCRIPT_COLLECTION_PATH,
          query: scriptListQueryValues(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertScriptListResponse(payload as ScriptListResponse, {
              field: 'script.list',
              requestId,
            });
          },
        }),
      create: (data, options = {}) =>
        request<ScriptResponse>({
          method: 'POST',
          path: SCRIPT_COLLECTION_PATH,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertScriptResponse(payload as ScriptResponse, {
              field: 'script.create',
              requestId,
            });
          },
        }),
      updatePlaces: (scriptId, data, options = {}) =>
        request<ScriptResponse>({
          method: 'POST',
          path: scriptPlacesPath(scriptId),
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertScriptResponse(payload as ScriptResponse, {
              field: 'script.places',
              requestId,
            });
          },
        }),
      remove: (scriptId, options = {}) =>
        request<DeleteScriptResponse>({
          method: 'DELETE',
          path: scriptItemPath(scriptId),
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertDeleteScriptResponse(payload as DeleteScriptResponse, {
              field: 'script.remove',
              requestId,
            });
          },
        }),
      buildLoader: (data, options = {}) =>
        request<BuildLoaderResponse>({
          method: 'POST',
          path: SCRIPT_LOADER_PATH,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertBuildLoaderResponse(payload as BuildLoaderResponse, {
              field: 'script.loader',
              requestId,
            });
          },
        }),
      obfuscate: (data, options = {}) =>
        request<ObfuscateScriptResponse>({
          method: 'POST',
          path: SCRIPT_OBFUSCATE_PATH,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertObfuscateScriptResponse(payload as ObfuscateScriptResponse, {
              field: 'script.obfuscate',
              requestId,
            });
          },
        }),
    },
    security: {
      statusQuery: securityStatusQuery,
      status: (query, options = {}) =>
        request<SecurityStatusResponse>({
          method: 'GET',
          path: SECURITY_PATHS.status,
          query: securityStatusQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityStatus(payload as SecurityStatusResponse, {
              field: 'security.status',
              requestId,
            });
          },
        }),
      events: (query = {}, options = {}) =>
        request<SecurityEventsPage>({
          method: 'GET',
          path: SECURITY_PATHS.events,
          query: listSecurityEventsQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityEventPage(payload as SecurityEventsPage, {
              field: 'security.events',
              requestId,
            });
          },
        }),
      blocks: (query = {}, options = {}) =>
        request<SecurityBlocksPage>({
          method: 'GET',
          path: SECURITY_PATHS.blocks,
          query: listSecurityBlocksQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityBlockPage(payload as SecurityBlocksPage, {
              field: 'security.blocks',
              requestId,
            });
          },
        }),
      block: (data, options = {}) =>
        request<IpBlockRecord>({
          method: 'POST',
          path: SECURITY_PATHS.blocks,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertSecurityBlock(payload as IpBlockRecord, {
              field: 'security.block',
              requestId,
            });
          },
        }),
      unblock: (blockId, data, options = {}) =>
        request<IpBlockRecord>({
          method: 'POST',
          path: `${SECURITY_PATHS.blocks}/${encodeURIComponent(blockId)}/revoke`,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertSecurityBlock(payload as IpBlockRecord, {
              field: 'security.block.revoke',
              requestId,
            });
          },
        }),
      extend: (blockId, data, options = {}) =>
        request<IpBlockRecord>({
          method: 'POST',
          path: `${SECURITY_PATHS.blocks}/${encodeURIComponent(blockId)}/extend`,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertSecurityBlock(payload as IpBlockRecord, {
              field: 'security.block.extend',
              requestId,
            });
          },
        }),
      ip: (query, options = {}) =>
        request<SecurityIpQueryResponse>({
          method: 'GET',
          path: SECURITY_PATHS.ip,
          query: queryValue({ address: query.address }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityIpQuery(payload as SecurityIpQueryResponse, {
              field: 'security.ip',
              requestId,
            });
          },
        }),
      stats: (options = {}) =>
        request<SecurityStatsResponse>({
          method: 'GET',
          path: SECURITY_PATHS.stats,
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityStats(payload as SecurityStatsResponse, {
              field: 'security.stats',
              requestId,
            });
          },
        }),
      policy: (options = {}) =>
        request<SecurityPolicyResponse>({
          method: 'GET',
          path: SECURITY_PATHS.policy,
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityPolicy((payload as SecurityPolicyResponse).policy, {
              field: 'security.policy',
              requestId,
            });
          },
        }),
      updatePolicy: (data, options = {}) =>
        request<SecurityPolicyResponse>({
          method: 'PUT',
          path: SECURITY_PATHS.policy,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertSecurityPolicy((payload as SecurityPolicyResponse).policy, {
              field: 'security.policy.update',
              requestId,
            });
          },
        }),
      preview: (data, options = {}) =>
        request<SecurityBlockPagePreviewResponse>({
          method: 'POST',
          path: SECURITY_PATHS.preview,
          body: data,
          ...(options.idempotencyKey === undefined
            ? {}
            : { idempotencyKey: options.idempotencyKey }),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          guard: (payload, requestId) => {
            assertBlockPagePreview(payload as SecurityBlockPagePreviewResponse, {
              field: 'security.preview',
              requestId,
            });
          },
        }),
      actions: (query = {}, options = {}) =>
        request<SecurityActionsPage>({
          method: 'GET',
          path: SECURITY_PATHS.actions,
          query: listSecurityActionsQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityActionPage(payload as SecurityActionsPage, {
              field: 'security.actions',
              requestId,
            });
          },
        }),
      notifications: (query = {}, options = {}) =>
        request<SecurityNotificationsResponse>({
          method: 'GET',
          path: SECURITY_PATHS.notifications,
          query: listSecurityNotificationsQuery(query),
          ...(options.signal === undefined ? {} : { signal: options.signal }),
          ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
          guard: (payload, requestId) => {
            assertSecurityNotificationPage(payload as SecurityNotificationsResponse, {
              field: 'security.notifications',
              requestId,
            });
          },
        }),
      acknowledgeNotification: (notificationId, options = {}) =>
        notificationAction(notificationId, 'acknowledge', options),
      dismissNotification: (notificationId, options = {}) =>
        notificationAction(notificationId, 'dismiss', options),
      allowlist: {
        list: (options = {}) =>
          request<SecurityAllowlistResponse>({
            method: 'GET',
            path: SECURITY_PATHS.allowlist,
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            ...(options.timeoutMs === undefined ? {} : { timeoutMs: options.timeoutMs }),
            guard: (payload, requestId) => {
              const page = payload as SecurityAllowlistResponse;
              if (!Array.isArray(page.items)) {
                throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
                  details: { field: 'security.allowlist.items', request_id: requestId },
                });
              }
            },
          }),
        add: (data, options = {}) =>
          request<AllowlistEntry>({
            method: 'POST',
            path: SECURITY_PATHS.allowlist,
            body: data,
            ...(options.idempotencyKey === undefined
              ? {}
              : { idempotencyKey: options.idempotencyKey }),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
            guard: (payload, requestId) => {
              const entry = payload as AllowlistEntry;
              if (typeof entry.label !== 'string' || entry.label.trim() === '') {
                throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
                  details: { field: 'security.allowlist.label', request_id: requestId },
                });
              }
            },
          }),
        remove: (allowlistId, data, options = {}) =>
          request<void>({
            method: 'POST',
            path: `${SECURITY_PATHS.allowlist}/${encodeURIComponent(allowlistId)}/remove`,
            body: data,
            ...(options.idempotencyKey === undefined
              ? {}
              : { idempotencyKey: options.idempotencyKey }),
            ...(options.signal === undefined ? {} : { signal: options.signal }),
          }),
      },
    },
  };
}

async function resolveToken(provider: ApiClientConfig['accessToken']): Promise<string | undefined> {
  if (provider === undefined) {
    return undefined;
  }
  const value = await provider();
  return value === null || value === undefined || value === '' ? undefined : value;
}

type XmlHttpRequestConstructor = new () => XhrLike;

function defaultXhrFactory(): XhrLike {
  const holder = globalThis as Record<string, unknown>;
  const ctor = holder['XMLHttpRequest'] as XmlHttpRequestConstructor | undefined;
  if (typeof ctor !== 'function') {
    throw new ApiError(ERROR_CODES.CONFIGURATION_INVALID, {
      details: { field: 'xhrFactory', reason: 'unavailable' },
    });
  }
  return new ctor();
}

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === '') {
    return {};
  }
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError(ERROR_CODES.UNSUPPORTED_SERVER_STATE, {
      details: { field: 'response_body', reason: 'invalid_json', http_status: response.status },
    });
  }
}

export { collectPages, firstPage, iteratePages, mergeQuery };
export type { FetchImplementation };
